import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import SuperAdminLayout from "@/components/admin/super-admin-layout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  FileText, 
  Trash2, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Calendar,
  Users,
  RefreshCw,
  Download
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";

interface ImportSession {
  id: number;
  sessionId: string;
  userId: number;
  tenantId: number;
  totalRecords: number;
  successfulRecords: number;
  failedRecords: number;
  createdRecords: Array<{
    type: string;
    ids: number[];
  }>;
  errorLog: Array<{
    timestamp?: string;
    error?: string;
    action?: string;
    deletedBy?: string;
    results?: any;
  }>;
  status: string;
  createdAt: string;
  updatedAt: string;
  // Metadata from API
  hoursAgo: number;
  canDelete: boolean;
  deletableRecords: number;
  cooldownEndsAt: string;
}

interface ImportSessionsResponse {
  sessions: ImportSession[];
  cooldownHours: number;
  totalFound: number;
  showing: number;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'partial':
      return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'failed':
      return 'bg-rose-100 text-rose-700 border-rose-200';
    case 'in_progress':
      return 'bg-blue-100 text-blue-700 border-blue-200';
    case 'bulk_deleted':
      return 'bg-slate-100 text-slate-700 border-slate-200';
    default:
      return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

const getStatusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckCircle className="h-3 w-3" />;
    case 'partial':
      return <AlertTriangle className="h-3 w-3" />;
    case 'failed':
      return <XCircle className="h-3 w-3" />;
    case 'in_progress':
      return <Clock className="h-3 w-3" />;
    case 'bulk_deleted':
      return <Trash2 className="h-3 w-3" />;
    default:
      return <Clock className="h-3 w-3" />;
  }
};

const ImportHistoryPage = () => {
  const [cooldownHours, setCooldownHours] = useState(48);
  const { toast } = useToast();

  // Fetch import sessions
  const { data: importData, isLoading, refetch } = useQuery<ImportSessionsResponse>({
    queryKey: ["/api/admin/import-sessions", cooldownHours],
    queryFn: async () => {
      const response = await apiRequest(`/api/admin/import-sessions?cooldownHours=${cooldownHours}&limit=50`, {
        method: "GET",
      });
      return response;
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiRequest(`/api/admin/import-sessions/${sessionId}/bulk-delete?cooldownHours=${cooldownHours}`, {
        method: "DELETE",
      });
      return response;
    },
    onSuccess: (data) => {
      toast({
        title: "Bulk deletion successful",
        description: data.message || `Successfully deleted ${data.totalDeleted} records`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/import-sessions"] });
    },
    onError: (error: any) => {
      toast({
        title: "Bulk deletion failed",
        description: error.response?.data?.error || "Failed to delete import session records",
        variant: "destructive",
      });
    },
  });

  const handleBulkDelete = (sessionId: string) => {
    bulkDeleteMutation.mutate(sessionId);
  };

  if (isLoading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      </SuperAdminLayout>
    );
  }

  const sessions = importData?.sessions || [];

  return (
    <SuperAdminLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-800" data-testid="page-title">
              Import History
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Manage recent data imports and bulk deletion within cooldown period
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4 md:mt-0">
            <Button
              onClick={() => refetch()}
              variant="outline"
              size="sm"
              data-testid="button-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Cooldown Period:</span>
              <select
                value={cooldownHours}
                onChange={(e) => setCooldownHours(parseInt(e.target.value))}
                className="border rounded px-2 py-1 text-sm"
                data-testid="select-cooldown"
              >
                <option value={24}>24 hours</option>
                <option value={48}>48 hours</option>
                <option value={72}>72 hours</option>
              </select>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Sessions</p>
                  <p className="text-2xl font-bold text-slate-800" data-testid="stat-total-sessions">
                    {importData?.totalFound || 0}
                  </p>
                </div>
                <FileText className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Deletable</p>
                  <p className="text-2xl font-bold text-emerald-600" data-testid="stat-deletable">
                    {sessions.filter(s => s.canDelete).length}
                  </p>
                </div>
                <Trash2 className="h-8 w-8 text-emerald-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Completed</p>
                  <p className="text-2xl font-bold text-blue-600" data-testid="stat-completed">
                    {sessions.filter(s => s.status === 'completed').length}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Failed</p>
                  <p className="text-2xl font-bold text-rose-600" data-testid="stat-failed">
                    {sessions.filter(s => s.status === 'failed').length}
                  </p>
                </div>
                <XCircle className="h-8 w-8 text-rose-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Import Sessions Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="h-5 w-5 mr-2" />
              Recent Import Sessions
            </CardTitle>
            <CardDescription>
              {importData?.showing} of {importData?.totalFound} sessions within {cooldownHours} hour cooldown period
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No import sessions found within the cooldown period</p>
                <p className="text-sm text-slate-400 mt-1">
                  Import sessions older than {cooldownHours} hours are not eligible for bulk deletion
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Session ID</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Records</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Cooldown Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((session) => (
                      <TableRow key={session.id} data-testid={`row-session-${session.sessionId}`}>
                        <TableCell className="font-mono text-sm">
                          {session.sessionId.substring(0, 8)}...
                        </TableCell>
                        <TableCell>
                          <Badge 
                            className={`${getStatusColor(session.status)} flex items-center gap-1 w-fit`}
                          >
                            {getStatusIcon(session.status)}
                            {session.status.charAt(0).toUpperCase() + session.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div>Total: <span className="font-medium">{session.totalRecords}</span></div>
                            <div className="text-emerald-600">Success: {session.successfulRecords}</div>
                            {session.failedRecords > 0 && (
                              <div className="text-rose-600">Failed: {session.failedRecords}</div>
                            )}
                            {session.deletableRecords > 0 && (
                              <div className="text-blue-600">Deletable: {session.deletableRecords}</div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <div className="font-medium">
                              {formatDistanceToNow(new Date(session.createdAt), { addSuffix: true })}
                            </div>
                            <div className="text-slate-500">
                              {new Date(session.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {session.canDelete ? (
                            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">
                              <Clock className="h-3 w-3 mr-1" />
                              {Math.max(0, cooldownHours - session.hoursAgo)}h remaining
                            </Badge>
                          ) : (
                            <Badge className="bg-rose-100 text-rose-700 border-rose-200">
                              <XCircle className="h-3 w-3 mr-1" />
                              Expired
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {session.canDelete && session.deletableRecords > 0 && (
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    disabled={bulkDeleteMutation.isPending}
                                    data-testid={`button-delete-${session.sessionId}`}
                                  >
                                    <Trash2 className="h-3 w-3 mr-1" />
                                    Delete
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Bulk Delete Import Session</AlertDialogTitle>
                                    <AlertDialogDescription className="space-y-2">
                                      <p>
                                        Are you sure you want to delete all records from this import session?
                                      </p>
                                      <div className="bg-slate-50 p-3 rounded-md text-sm">
                                        <strong>Session:</strong> {session.sessionId}<br />
                                        <strong>Records to delete:</strong> {session.deletableRecords}<br />
                                        <strong>Status:</strong> {session.status}
                                      </div>
                                      <p className="text-rose-600 font-medium">
                                        This action cannot be undone!
                                      </p>
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleBulkDelete(session.sessionId)}
                                      className="bg-rose-600 hover:bg-rose-700"
                                      data-testid={`confirm-delete-${session.sessionId}`}
                                    >
                                      Delete {session.deletableRecords} Records
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                            {!session.canDelete && (
                              <span className="text-xs text-slate-400">
                                Cooldown expired
                              </span>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Usage Instructions */}
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="text-blue-800 flex items-center">
              <AlertTriangle className="h-5 w-5 mr-2" />
              Import History & Bulk Deletion Guidelines
            </CardTitle>
          </CardHeader>
          <CardContent className="text-blue-700 space-y-3">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium mb-2">Cooldown Period</h4>
                <ul className="text-sm space-y-1">
                  <li>• Import sessions can only be bulk deleted within the cooldown period</li>
                  <li>• Default cooldown is 48 hours from import time</li>
                  <li>• After cooldown expires, individual record deletion is required</li>
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Safety Features</h4>
                <ul className="text-sm space-y-1">
                  <li>• Only admin users can perform bulk deletions</li>
                  <li>• All deletions are logged for audit purposes</li>
                  <li>• Confirmation dialog prevents accidental deletions</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </SuperAdminLayout>
  );
};

export default ImportHistoryPage;